import { useState } from 'react';
import { api } from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { History, RotateCcw } from 'lucide-react';

export function TemplateVersionHistory({ ttype, tid, name, onRestored }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setData(null);
    try { const r = await api.get(`/templates/${ttype}/${tid}/versions`); setData(r.data); }
    catch { setData({ current_version: 1, versions: [] }); }
  };

  const openDialog = () => { setOpen(true); load(); };

  const restore = async (version) => {
    if (!confirm(`Restore "${name}" to version ${version}? The current version will be saved to history first.`)) return;
    setBusy(true);
    try {
      const r = await api.post(`/templates/${ttype}/${tid}/restore/${version}`);
      toast.success(`Restored to v${version} — now saved as v${r.data.version}`);
      setOpen(false);
      onRestored?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Restore failed'); }
    finally { setBusy(false); }
  };

  return (
    <>
      <button onClick={openDialog} data-testid={`version-history-btn-${tid}`}
        className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-[#00C8FF] transition">
        <History className="w-3.5 h-3.5" /> History
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-regen-card border-[var(--c-hair)] max-w-md" data-testid="version-history-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg">Version History</DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground truncate">{name}</DialogDescription>
          </DialogHeader>

          {!data && <div className="py-8 text-center text-sm text-muted-foreground">Loading…</div>}
          {data && (
            <div className="space-y-2 mt-2 max-h-[60vh] overflow-y-auto">
              <div className="flex items-center justify-between rounded-xl bg-[#00C8FF]/10 border border-[#00C8FF]/20 px-3.5 py-3" data-testid="version-current">
                <div>
                  <div className="text-sm font-semibold text-[#00C8FF]">Version {data.current_version} · Current</div>
                  <div className="text-[11px] text-muted-foreground">This is the live template.</div>
                </div>
              </div>

              {data.versions.length === 0 && (
                <div className="text-xs text-muted-foreground text-center py-4" data-testid="version-empty">
                  No previous versions yet. Edits create restorable snapshots automatically.
                </div>
              )}

              {data.versions.map(v => (
                <div key={v.version} className="flex items-center justify-between rounded-xl bg-white/[0.03] border border-[var(--c-hair)] px-3.5 py-3" data-testid={`version-row-${v.version}`}>
                  <div className="min-w-0">
                    <div className="text-sm font-medium">Version {v.version}</div>
                    <div className="text-[11px] text-muted-foreground">
                      {(v.created_at || '').slice(0, 10)}{v.restored_from_version ? ` · restored from v${v.restored_from_version}` : ''}
                    </div>
                  </div>
                  <Button onClick={() => restore(v.version)} disabled={busy} variant="outline" size="sm"
                    data-testid={`version-restore-${v.version}`}
                    className="h-8 border-[var(--c-hair)] text-xs">
                    <RotateCcw className="w-3.5 h-3.5 mr-1" /> Restore
                  </Button>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
